import {
  getTeamIdFromNhlEvent,
  type GoalEvent,
  type HitEvent,
  type NhlBoxGameState,
  type NhlEvent,
  type NhlGameDataResponse,
  type NhlPlayByPlayResponse,
  type NhlStatKey,
  type NhlTeamGameModel,
  type PenaltyEvent,
  type ShotMissedEvent,
  type ShotSavedEvent,
} from '@statmuse/core/gamera'
import { atom, map, computed } from 'nanostores'
import { last, every } from 'lodash-es'
import type { NhlPlayersMediaQuery } from '@statmuse/core/../graphql/graphql'

export const awayTeamId = atom(0)
export const homeTeamId = atom(0)

export const gameScore = map({ home: 0, away: 0 })

export const lineScore = atom<{
  away?: NhlGameDataResponse<NhlStatKey>['awayTeam']['lineScore']
  home?: NhlGameDataResponse<NhlStatKey>['awayTeam']['lineScore']
}>({})

export const playerStats = map<{
  away?: NonNullable<NhlTeamGameModel<NhlStatKey>['stats']>['player']
  home?: NonNullable<NhlTeamGameModel<NhlStatKey>['stats']>['player']
}>({})

export const teamStats = map<{
  away?: NonNullable<NhlTeamGameModel<NhlStatKey>['stats']>['team']
  home?: NonNullable<NhlTeamGameModel<NhlStatKey>['stats']>['team']
}>({})

export const players = map<
  Record<
    string,
    NonNullable<NhlPlayersMediaQuery['nhl']['players']>[number] | undefined
  >
>({})

export const plays = atom<NhlEvent[]>([])

export const periodClock = computed(plays, ($plays) => {
  const lastPlay = last($plays)

  return {
    period: lastPlay?.period,
    clock: lastPlay?.type === 'endPeriod' ? '0:00' : lastPlay?.clock ?? '20:00',
    endOfQuarter: lastPlay?.type === 'endPeriod',
  }
})

export const isEmptyNet = computed(
  [plays, players, periodClock],
  ($plays, $players, $periodClock) => {
    const lastPlay = $plays
      .filter((p) => p.period === $periodClock.period)
      .findLast((p) => p.awayOnIcePlayerIds && p.homeOnIcePlayerIds)
    const awayPlayers = lastPlay?.awayOnIcePlayerIds ?? []
    const homePlayers = lastPlay?.homeOnIcePlayerIds ?? []

    return {
      away: every(
        awayPlayers.map((id) => $players[id]?.position),
        (x) => x !== 'GOALIE',
      ),
      home: every(
        homePlayers.map((id) => $players[id]?.position),
        (x) => x !== 'GOALIE',
      ),
    }
  },
)

export const isPowerPlay = computed(
  [plays, periodClock, awayTeamId, homeTeamId],
  ($plays, $periodClock, $awayTeamId, $homeTeamId) => {
    const lastPlay = $plays
      .filter((p) => p.period === $periodClock.period)
      .findLast((p) => p.strength)

    const teamId = getTeamIdFromNhlEvent(lastPlay)

    return {
      away:
        (lastPlay?.strength === 'powerPlay' && teamId === $awayTeamId) ||
        (lastPlay?.strength === 'shortHanded' && teamId === $homeTeamId),
      home:
        (lastPlay?.strength === 'powerPlay' && teamId === $homeTeamId) ||
        (lastPlay?.strength === 'shortHanded' && teamId === $awayTeamId),
    }
  },
)

export const selectedId = atom<
  { id: number; alignment: 'home' | 'away' } | undefined
>()

export const selectedPlayer = computed(
  [selectedId, players, playerStats, plays],
  ($selectedId, $players, $stats, $plays) => {
    if (!$selectedId) return undefined
    const player = $players[$selectedId.id + '']

    const lookupKey = $stats[$selectedId.alignment]?.splits?.[0].splits?.find(
      (s) => s.playerId === $selectedId.id,
    )?.statsLookupKey

    const stats = $stats[$selectedId.alignment]?.statsLookup[lookupKey ?? '']

    const events = $plays.filter(
      (
        p,
      ): p is
        | GoalEvent
        | ShotMissedEvent
        | ShotSavedEvent
        | HitEvent
        | PenaltyEvent =>
        (p.type === 'goal' ||
          p.type === 'shotMissed' ||
          p.type === 'shotSaved' ||
          p.type === 'hit' ||
          p.type === 'penalty') &&
        (p.playerId === $selectedId.id ||
          p.deliveredPlayerId === $selectedId.id),
    )

    return {
      player,
      stats,
      events,
      alignment: $selectedId.alignment,
    }
  },
)

export const init = (props: {
  gameData?: NhlGameDataResponse<NhlStatKey>
  playByPlay?: NhlPlayByPlayResponse
  players?: Record<
    string,
    NonNullable<NhlPlayersMediaQuery['nhl']['players']>[number] | undefined
  >
}) => {
  awayTeamId.set(props.gameData?.awayTeam.teamId ?? 0)
  homeTeamId.set(props.gameData?.homeTeam.teamId ?? 0)
  gameScore.set({
    away: props.gameData?.awayTeam.score ?? 0,
    home: props.gameData?.homeTeam.score ?? 0,
  })
  lineScore.set({
    away: props.gameData?.awayTeam.lineScore,
    home: props.gameData?.homeTeam.lineScore,
  })
  playerStats.set({
    away: props.gameData?.awayTeam.stats?.player,
    home: props.gameData?.homeTeam.stats?.player,
  })
  teamStats.set({
    away: props.gameData?.awayTeam.stats?.team,
    home: props.gameData?.homeTeam.stats?.team,
  })
  players.set(props.players ?? {})

  const shootoutEvents = props.playByPlay?.events?.filter((e) => e.period === 5)
  const shootoutScore = { away: 0, home: 0 }

  const awayId = props.gameData?.awayTeam.teamId
  const homeId = props.gameData?.homeTeam.teamId

  const soEvents =
    shootoutEvents?.map((e) => {
      return {
        ...e,
        awayShootoutScore:
          e.type === 'goal' && e.teamId === awayId
            ? (shootoutScore.away += 1)
            : shootoutScore.away,
        homeShootoutScore:
          e.type === 'goal' && e.teamId === homeId
            ? (shootoutScore.home += 1)
            : shootoutScore.home,
      }
    }) ?? []

  plays.set([
    ...(props.playByPlay?.events?.filter((e) => e.period !== 5) ?? []),
    ...soEvents,
  ])
}

export const update = (data: NhlBoxGameState) => {
  gameScore.set(data.gameScore)
  playerStats.set(data.playerStats)
  teamStats.set(data.teamStats)
  lineScore.set(data.lineScore)

  plays.set([
    ...plays.get().filter((e) => e.period !== data.currentPeriod),
    ...data.lastEvents,
  ])
}
